import * as Immutable from 'immutable';
import * as t from 'io-ts';
import { createActions } from 'redux-actions';

import { ENDPOINTS_ALPHAS, ENDPOINTS_USER } from '../config';
import { localeData } from '../locales/data';
import { AlphasListingData, AlphaColumn } from '../reducers/AlphasReducer/AlphasReducer.types';
import { addFlashMessageWrapper, defaultErrorMessage, globalFetch, ioTsValidationError, isAction } from '../shared/helpers/fetch-helper';
import { logErrorService } from '../shared/services';

import { addFlashMessages, removeFlashLocations } from './FlashMessagesActions';
import { invalidRequest } from './GenericActions';

const postUserList = async (listName: string, alphas: string[], failureDispatch: any, dispatch: any): Promise<object> => {
  const alphasList = Immutable.fromJS(alphas);
  const requestBody = Immutable.fromJS({
    type: 'LIST',
    name: listName,
    alphas: alphasList,
  });

  return globalFetch(
    `${ENDPOINTS_ALPHAS.tags}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify(requestBody),
    },
    dispatch,
    failureDispatch
  );
};

const patchUserList = async (
  callback: any,
  tagId: string,
  listName: string,
  alphas: string[],
  op = 'add',
  dispatch: any
): Promise<object> => {
  let requestBody;
  if (op === 'rename') {
    requestBody = Immutable.fromJS({
      name: listName,
    });
  } else if (listName === '') {
    const alphasList = Immutable.fromJS(alphas);
    requestBody = Immutable.fromJS({
      op: op,
      alphas: alphasList,
    });
  } else if (alphas.length === 0) {
    requestBody = Immutable.fromJS({
      op: op,
      name: listName,
    });
  } else {
    const alphasList = Immutable.fromJS(alphas);
    requestBody = Immutable.fromJS({
      op: op,
      name: listName,
      alphas: alphasList,
    });
  }

  return globalFetch(
    `${ENDPOINTS_ALPHAS.tags}/${tagId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify(requestBody),
    },
    dispatch,
    callback
  );
};

export const {
  setSelectedColumnsTags,
  changeInnerCorrelationColors,
  resetSelectedColumnsTags,
  requestUserTags,
  errorUserTags,
  setUserTags,
  requestUserlistOptions,
  responseUserlistOptionsSuccess,
  responseUserlistOptionsFailure,
  requestCreateUserlist,
  doneCreateUserlist,
  errorCreateUserlist,
  requestUpdateUserlist,
  doneUpdateUserlist,
  errorUpdateUserlist,
  requestIdTag,
  responseIdTagFailure,
  responseIdTagSuccess,
  requestAlphaTag,
  responseAlphaTagFailure,
  responseAlphaTagSuccess,
  requestInnerCorrelationTag,
  responseInnerCorrelationTagFailure,
  responseInnerCorrelationTagSuccess,
  requestSelfCorrelationTag,
  responseSelfCorrelationTagFailure,
  responseSelfCorrelationTagSuccess,
  requestDeleteTag,
  responseDeleteTagFailure,
  responseDeleteTagSuccess,
  requestTagAlphas,
  responseTagAlphasFailure,
  responseTagAlphasSuccess,
  tooManyAlphasInList,
  cleanAlphasListMessage,
} = createActions(
  {
    SET_SELECTED_COLUMNS_TAGS: (selectedColumns: AlphaColumn) => ({ selectedColumns }),
  },
  'CHANGE_INNER_CORRELATION_COLORS',
  'RESET_SELECTED_COLUMNS_TAGS',
  'REQUEST_USER_TAGS',
  'ERROR_USER_TAGS',
  'SET_USER_TAGS',
  'REQUEST_USERLIST_OPTIONS',
  'RESPONSE_USERLIST_OPTIONS_SUCCESS',
  'RESPONSE_USERLIST_OPTIONS_FAILURE',
  'REQUEST_CREATE_USERLIST',
  'DONE_CREATE_USERLIST',
  'ERROR_CREATE_USERLIST',
  'REQUEST_UPDATE_USERLIST',
  'DONE_UPDATE_USERLIST',
  'ERROR_UPDATE_USERLIST',
  'REQUEST_ID_TAG',
  'RESPONSE_ID_TAG_FAILURE',
  'RESPONSE_ID_TAG_SUCCESS',
  'REQUEST_ALPHA_TAG',
  'RESPONSE_ALPHA_TAG_FAILURE',
  'RESPONSE_ALPHA_TAG_SUCCESS',
  'REQUEST_INNER_CORRELATION_TAG',
  'RESPONSE_INNER_CORRELATION_TAG_FAILURE',
  'RESPONSE_INNER_CORRELATION_TAG_SUCCESS',
  'REQUEST_SELF_CORRELATION_TAG',
  'RESPONSE_SELF_CORRELATION_TAG_FAILURE',
  'RESPONSE_SELF_CORRELATION_TAG_SUCCESS',
  'REQUEST_DELETE_TAG',
  'RESPONSE_DELETE_TAG_FAILURE',
  'RESPONSE_DELETE_TAG_SUCCESS',
  'REQUEST_TAG_ALPHAS',
  'RESPONSE_TAG_ALPHAS_FAILURE',
  'RESPONSE_TAG_ALPHAS_SUCCESS',
  'TOO_MANY_ALPHAS_IN_LIST',
  'CLEAN_ALPHAS_LIST_MESSAGE'
);

export const optionsUserTags =
  () =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestUserlistOptions());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.tags}`,
      {
        method: 'OPTIONS',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseUserlistOptionsFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (json && json.actions && json.actions.POST.name.choices) {
          return dispatch(responseUserlistOptionsSuccess(json.actions.POST.name.choices));
        } else if (isAction(json, 'RESPONSE_USERLIST_OPTIONS_FAILURE')) {
          return json;
        } else {
          dispatch(addFlashMessages([{ msg: 'alphaTags.user.lists.error', defaultMessage: localeData.en['alphaTags.user.lists.error'] }]));
          logErrorService(json, json);

          return dispatch(responseUserlistOptionsFailure());
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch);

        return dispatch(responseUserlistOptionsFailure());
      });
  };

export const getUserTags =
  (limit = 0) =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestUserTags());

    return globalFetch(
      `${ENDPOINTS_USER.user}/self/tags?limit=9&offset=${limit}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      },
      dispatch,
      () => dispatch(errorUserTags())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'ERROR_USER_TAGS')) {
          return json;
        } else if (json.results) {
          return dispatch(setUserTags(json));
        } else {
          dispatch(addFlashMessages([{ msg: 'alphaTags.user.list.error', defaultMessage: localeData.en['alphaTags.user.list.error'] }]));
          logErrorService(json, json);

          return dispatch(errorUserTags());
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch);

        return dispatch(errorUserTags());
      });
  };

export const createUserList =
  (listName: string, alphas: string[] = []) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();

    if (state.getIn(['tags', 'lists', 'fetchingData']) === true) {
      return dispatch(invalidRequest());
    }
    dispatch(requestCreateUserlist());

    return postUserList(listName, alphas, () => dispatch(errorCreateUserlist()), dispatch)
      .then(async (result: any): Promise<object> => {
        if (isAction(result, 'ERROR_CREATE_USERLIST')) {
          dispatch(addFlashMessageWrapper('alphaTags.create.error', { location: 'alpha-list' }));

          return dispatch(errorCreateUserlist());
        } else if (state.hasIn(['user', 'data', 'id'])) {
          dispatch(getUserTags());
          dispatch(
            addFlashMessages([{ msg: 'alphasMain.created.list', defaultMessage: localeData.en['alphasMain.created.list'] }], {
              location: 'alpha-list',
              type: 'info',
            })
          );
          setTimeout(() => dispatch(removeFlashLocations(['alpha-list'])), 3000);

          return dispatch(optionsUserTags()).then(() => dispatch(doneCreateUserlist()));
        }

        return dispatch(errorCreateUserlist());
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch, 'alpha-list');
        setTimeout(() => dispatch(removeFlashLocations(['alpha-list'])), 3000);

        return dispatch(errorCreateUserlist());
      });
  };

function filterQuery(filters: any) {
  if (!filters) {
    return '';
  }

  return Object.keys(filters)
    .map(key => {
      const value = filters[key];
      if (!value) {
        return '';
      }

      if (/&+/.exec(value)) {
        let rangeValues: string = '';
        value.split('&').forEach((val: string) => (rangeValues = rangeValues.concat(`${key}${val}&`)));

        return rangeValues.substring(0, rangeValues.length - 1);
      } else {
        return `${key}${value}`;
      }
    })
    .join('&');
}

export const getInnerCorrelationTag =
  (id: string | undefined) =>
  async (dispatch: any): Promise<object> => {
    if (id === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestInnerCorrelationTag());

    const callbackStatus400 = (response: any) => {
      response
        .json()
        .then((json: any) => {
          if (json) {
            dispatch(addFlashMessages(json, { intl: false }));
          }

          return dispatch(responseInnerCorrelationTagFailure());
        })
        .catch(() => dispatch(responseInnerCorrelationTagFailure()));
    };

    return globalFetch(
      `${ENDPOINTS_ALPHAS.tags}/${id}/correlations/inner`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseInnerCorrelationTagFailure()),
      {
        callbackStatus400,
        callbackAction: () => dispatch(getInnerCorrelationTag(id)),
        retryEmpty: true,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (!json) {
          return dispatch(responseSelfCorrelationTagFailure());
        } else if (isAction(json, 'RESPONSE_INNER_CORRELATION_TAG_FAILURE')) {
          return json;
        } else {
          return dispatch(responseInnerCorrelationTagSuccess(json));
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch);

        return dispatch(responseInnerCorrelationTagFailure());
      });
  };

export const getSelfCorrelationTag =
  (id: string) =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestSelfCorrelationTag());
    const callbackStatus400 = (response: any) => {
      response
        .json()
        .then((json: any) => {
          if (json) {
            dispatch(addFlashMessages(json, { intl: false }));
          }

          return dispatch(responseSelfCorrelationTagFailure());
        })
        .catch(() => dispatch(responseSelfCorrelationTagFailure()));
    };

    return globalFetch(
      `${ENDPOINTS_ALPHAS.tags}/${id}/correlations/self`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseSelfCorrelationTagFailure()),
      {
        callbackStatus400,
        callbackAction: () => dispatch(getSelfCorrelationTag(id)),
        retryEmpty: true,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (!json) {
          return dispatch(responseSelfCorrelationTagFailure());
        } else if (isAction(json, 'RESPONSE_SELF_CORRELATION_TAG_FAILURE')) {
          return json;
        } else {
          return dispatch(responseSelfCorrelationTagSuccess(json));
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch);

        return dispatch(responseSelfCorrelationTagFailure());
      });
  };

export const getTagList =
  (id: string) =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestIdTag());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.tags}/${id}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseIdTagFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (json.detail) {
          logErrorService(json.detail, json.detail);
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }]));

          return dispatch(responseIdTagFailure());
        } else if (isAction(json, 'RESPONSE_ID_TAG_FAILURE')) {
          return json;
        } else {
          return dispatch(responseIdTagSuccess(json));
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch);

        return dispatch(responseIdTagFailure());
      });
  };

export function getTagAlphas(id: string | undefined, limit: number | undefined, offset: number | undefined, filters: any) {
  return async (dispatch: any): Promise<object> => {
    if (id === undefined || limit === undefined || offset === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestTagAlphas());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.tags}/${id}/alphas?limit=${limit}&offset=${offset}&${filterQuery(filters)}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseTagAlphasFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (json.details) {
          logErrorService(json.details, json.details);
          dispatch(addFlashMessages([{ msg: json.details, defaultMessage: json.details }]));

          return dispatch(responseTagAlphasFailure());
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }]));

          return dispatch(responseTagAlphasFailure());
        } else if (isAction(json, 'RESPONSE_TAG_ALPHAS_FAILURE')) {
          return json;
        } else if (!t.exact(AlphasListingData).decode(json).isRight()) {
          dispatch(addFlashMessages([{ msg: json, defaultMessage: json }]));
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasListingData);

          return dispatch(responseTagAlphasFailure());
        } else {
          dispatch(getTagList(id));

          return dispatch(responseTagAlphasSuccess(json));
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch);

        return dispatch(responseTagAlphasFailure());
      });
  };
}

export const updateUserList =
  (tagId: string, listName: string, alphas: string[] = [], op = 'add') =>
  async (dispatch: any, getState: any): Promise<object> => {
    dispatch(requestUpdateUserlist());

    return patchUserList(() => dispatch(errorUpdateUserlist()), tagId, listName, alphas, op, dispatch)
      .then(async (json: any): Promise<object> => {
        if (json.details) {
          logErrorService(json.details, json.details);
          dispatch(
            addFlashMessages([{ msg: 'alphasMain.updating.list.error', defaultMessage: localeData.en['alphasMain.updating.list.error'] }], {
              location: 'alpha-list',
            })
          );
          setTimeout(() => dispatch(removeFlashLocations(['alpha-list'])), 3000);

          return dispatch(errorUpdateUserlist());
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);
          dispatch(
            addFlashMessages([{ msg: 'alphasMain.adding.list.error', defaultMessage: localeData.en['alphasMain.adding.list.error'] }], {
              location: 'alpha-list',
            })
          );
          setTimeout(() => dispatch(removeFlashLocations(['alpha-list'])), 3000);

          return dispatch(errorUpdateUserlist());
        } else if (isAction(json, 'ERROR_UPDATE_USERLIST')) {
          return json;
        } else {
          const state = getState();
          if (state.hasIn(['user', 'data', 'id'])) {
            dispatch(errorUpdateUserlist());
            dispatch(
              addFlashMessages([{ msg: 'alphasMain.updating.list', defaultMessage: localeData.en['alphasMain.updating.list'] }], {
                location: 'alpha-list',
                type: 'info',
              })
            );
            setTimeout(() => dispatch(removeFlashLocations(['alpha-list'])), 3000);
            dispatch(responseIdTagSuccess(json));

            return dispatch(getUserTags());
          } else {
            return dispatch(errorUpdateUserlist());
          }
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        defaultErrorMessage(dispatch, 'alpha-list');
        setTimeout(() => dispatch(removeFlashLocations(['alpha-list'])), 3000);

        return dispatch(errorUpdateUserlist());
      });
  };

export const deleteListTag =
  (tagId: string) =>
  async (dispatch: any): Promise<object> => {
    dispatch(requestDeleteTag());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.tags}/${tagId}`,
      {
        method: 'Delete',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseDeleteTagFailure()),
      {
        answerEmpty: true,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (json && json.type === 'RESPONSE_DELETE_TAG_FAILURE') {
          throw new Error('There was an error deleting the alphas list.');
        } else if (isAction(json, 'RESPONSE_DELETE_TAG_FAILURE')) {
          return json;
        } else if (json && json.details) {
          throw new Error(json.details);
        } else {
          return dispatch(responseDeleteTagSuccess());
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);
        dispatch(addFlashMessageWrapper('alphaTags.delete.error'));

        return dispatch(responseDeleteTagFailure());
      });
  };
